import Nav from 'components/calculators/Nav';
import ExpressLoanFirstStep from 'components/express-loan/ExpressLoanFirstStep';
import ExpressLoanSecondStep from 'components/express-loan/ExpressLoanSecondStep';
import ExpressLoanThirdStep from 'components/express-loan/ExpressLoanThirdStep';
import StepWizard, { type IStepWizardChildProps } from 'components/common/StepWizard';
import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { useHistory } from 'react-router-dom';
import routePaths from 'router/route-paths';
import { Col, Row } from 'react-flexbox-grid';
import useSearchParams from 'hooks/useSearchParams';
import { expressLoanProjectTypes } from './loan-project-types';

/** The navigation props StepWizard adds to each step when it renders it. */
type TWizardStepProps = Pick<IStepWizardChildProps, 'nextStep' | 'previousStep'>;

interface IExpressLoanStepPageProps {
  type: string | null;
  onSubmitStep: () => void;
  onBack: (callback?: () => void) => void;
}

interface IExpressLoanTab {
  label: string;
  component: ComponentType<IExpressLoanStepPageProps & TWizardStepProps>;
}

/**
 * Only for the step props: StepWizard clones every step with its own `nextStep`/`previousStep`
 * (`cloneElement(step, wizardProps)`), which replace these before a step renders.
 */
const wizardStepPropsPlaceholder: TWizardStepProps = {
  nextStep: () => {},
  previousStep: () => {},
};

interface IExpressLoanProps {
  type: string | null;
}

const tabs: IExpressLoanTab[] = [
  { label: 'express_loan.steps.first_step', component: ExpressLoanFirstStep },
  { label: 'express_loan.steps.second_step', component: ExpressLoanSecondStep },
  { label: 'express_loan.steps.third_step', component: ExpressLoanThirdStep },
];

const ExpressLoan = ({ type }: IExpressLoanProps) => {
  const [step, setStep] = useState(1);

  const onSubmitStep = useCallback(() => {
    window.scrollTo(0, 0);
    setStep(step + 1);
  }, [step]);

  const onBack = useCallback(
    (callback?: () => void) => {
      setStep(step - 1);
      if (typeof callback === 'function') callback();
    },
    [step],
  );

  return (
    <Row style={{ justifyContent: 'center' }}>
      <Col lg={10} xl={12}>
        <StepWizard initialStep={step} nav={<Nav {...{ tabs, step }} />}>
          {tabs.map(({ label, component: C }) => (
            <C key={label} {...{ type, onSubmitStep, onBack }} {...wizardStepPropsPlaceholder} />
          ))}
        </StepWizard>
      </Col>
    </Row>
  );
};

const ExpressLoanContainer = () => {
  const history = useHistory();
  const search = useSearchParams();
  const type = search.get('type');

  useEffect(() => {
    const selectedType = expressLoanProjectTypes.find((p) => p.type === type);
    if (!selectedType) history.push(routePaths.expressLoanQuickStart);
  }, [history, type]);

  return <ExpressLoan {...{ type }} />;
};

export default ExpressLoanContainer;
